import React, { useEffect, useMemo, useState } from "react";
import useJournal from "../../../hooks/useJournal";

import "../../../assets/css/admin/gestionJournal/AdminJournalPage.css";

// === Mappings selon le modèle Mongoose Journal ===
const TYPE_LABELS = {
  1: "Captcha",
  2: "Connexion Google",
  3: "Connexion Mail",
  4: "Connexion Facebook",
};

const TYPE_BADGE_CLASS = {
  1: "badgeCaptcha",
  2: "badgeGoogle",
  3: "badgeMail",
  4: "badgeFacebook",
};

const TYPE_ICON = {
  1: "bi-shield-lock",
  2: "bi-google",
  3: "bi-envelope",
  4: "bi-facebook",
};

const ARCHIVE_ARCHIVE = 1;
const ARCHIVE_NON = 2;

const PAGE_SIZE = 15;

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const normalized = (v) =>
  String(v ?? "")
    .toLowerCase()
    .trim();

export default function AdminJournalPage({ setAlert }) {
  const { getAllJournal, updateJournal } = useJournal();

  const [loading, setLoading] = useState(false);
  const [journaux, setJournaux] = useState([]);

  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [archiveFilter, setArchiveFilter] = useState("all"); // all | archived | notArchived
  const [sort, setSort] = useState("newest");

  const [page, setPage] = useState(1);

  const [pendingId, setPendingId] = useState(null);

  const fetchJournaux = async () => {
    setLoading(true);
    try {
      const res = await getAllJournal();
      const data = res?.data?.data || res?.data || [];
      setJournaux(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setJournaux([]);
      setAlert?.({
        type: "danger",
        message: "Impossible de récupérer la journalisation.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJournaux();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredJournaux = useMemo(() => {
    let list = [...(journaux || [])];

    if (q.trim()) {
      const query = normalized(q);
      list = list.filter((j) => normalized(j?.message).includes(query));
    }

    if (typeFilter !== "all") {
      list = list.filter(
        (j) => String(Number(j?.type)) === String(Number(typeFilter)),
      );
    }

    if (archiveFilter !== "all") {
      if (archiveFilter === "archived")
        list = list.filter((j) => Number(j?.archive) === ARCHIVE_ARCHIVE);
      if (archiveFilter === "notArchived")
        list = list.filter((j) => Number(j?.archive) !== ARCHIVE_ARCHIVE);
    }

    const dateOf = (j) => new Date(j?.date || j?.createdAt || 0);
    if (sort === "newest") {
      list.sort((a, b) => dateOf(b) - dateOf(a));
    } else if (sort === "oldest") {
      list.sort((a, b) => dateOf(a) - dateOf(b));
    }

    return list;
  }, [journaux, q, typeFilter, archiveFilter, sort]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredJournaux.length / PAGE_SIZE),
  );
  const safePage = Math.min(page, totalPages);
  const pagedJournaux = filteredJournaux.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  useEffect(() => {
    setPage(1);
  }, [q, typeFilter, archiveFilter, sort]);

  const handleToggleArchive = async (journal) => {
    const id = journal?._id;
    if (!id) return;

    const nextArchive =
      Number(journal.archive) === ARCHIVE_ARCHIVE
        ? ARCHIVE_NON
        : ARCHIVE_ARCHIVE;
    setPendingId(id);
    try {
      await updateJournal(id, { archive: nextArchive });
      setJournaux((prev) =>
        prev.map((j) =>
          String(j._id) === String(id) ? { ...j, archive: nextArchive } : j,
        ),
      );
      setAlert?.({
        type: "success",
        message:
          nextArchive === ARCHIVE_ARCHIVE
            ? "Entrée archivée."
            : "Entrée désarchivée.",
      });
    } catch (e) {
      console.error(e);
      setAlert?.({
        type: "danger",
        message: "Action impossible pour le moment.",
      });
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="adminJournalPage">
      <div className="container">
        <div className="adminJournalGrid">
          <section className="adminJournalCard adminJournalCardCenter">
            <div className="adminJournalHeader">
              <div>
                <div className="adminJournalTitle">
                  Journalisation des erreurs
                </div>
                <div className="adminJournalSubtitle">
                  {loading
                    ? "Chargement..."
                    : `${filteredJournaux.length} entrée(s)`}
                </div>
              </div>

              <button
                type="button"
                className="adminJournalBtn adminJournalBtnSoft"
                onClick={fetchJournaux}
                title="Rafraîchir"
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise" />
                <span>Rafraîchir</span>
              </button>
            </div>

            <div className="adminJournalFilters">
              <div className="adminJournalSearch">
                <i className="bi bi-search" aria-hidden="true" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Rechercher dans le message..."
                />
              </div>

              <div className="adminJournalSelects">
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="all">Tous les types</option>
                  {Object.entries(TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>

                <select
                  value={archiveFilter}
                  onChange={(e) => setArchiveFilter(e.target.value)}
                >
                  <option value="all">Tous les statuts</option>
                  <option value="notArchived">Non archivé</option>
                  <option value="archived">Archivé</option>
                </select>

                <select value={sort} onChange={(e) => setSort(e.target.value)}>
                  <option value="newest">Plus récents</option>
                  <option value="oldest">Plus anciens</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="adminJournalSkeletonWrap">
                <div className="adminJournalSpinner" />
              </div>
            ) : pagedJournaux.length === 0 ? (
              <div className="adminJournalEmpty">
                <i className="bi bi-journal-x" />
                <div>Aucune entrée trouvée.</div>
              </div>
            ) : (
              <div className="adminJournalTableWrap">
                <table className="adminJournalTable">
                  <thead>
                    <tr>
                      <th className="hideOnMobile">Date</th>
                      <th>Type</th>
                      <th className="hideOnMobile">Message</th>
                      <th className="hideOnMobile">Adresse IP</th>
                      <th className="hideOnMobile">Statut</th>
                      <th className="colActions">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {pagedJournaux.map((journal) => {
                      const archived =
                        Number(journal?.archive) === ARCHIVE_ARCHIVE;
                      const type = Number(journal?.type);

                      return (
                        <tr key={journal?._id}>
                          <td className="muted hideOnMobile nowrap">
                            {formatDate(journal?.date || journal?.createdAt)}
                          </td>

                          <td>
                            <span
                              className={`adminJournalBadge ${TYPE_BADGE_CLASS[type] || ""}`}
                            >
                              <i
                                className={`bi ${TYPE_ICON[type] || "bi-question-circle"} me-1`}
                              />
                              {TYPE_LABELS[type] || `Type ${type}`}
                            </span>

                            <div className="adminJournalMobileMeta">
                              <div>
                                <i className="bi bi-calendar3" />{" "}
                                {formatDate(
                                  journal?.date || journal?.createdAt,
                                )}
                              </div>
                              <div>
                                <i className="bi bi-chat-left-text" />{" "}
                                {journal?.message || "—"}
                              </div>
                              <div>
                                <i className="bi bi-archive" />{" "}
                                {archived ? "Archivé" : "Non archivé"}
                              </div>
                            </div>
                          </td>

                          <td className="adminJournalMessage hideOnMobile">
                            {journal?.message || "—"}
                          </td>
                          <td className="hideOnMobile">
                            <span className="adminJournalIp">
                              <i className="bi bi-globe2 me-1" />
                              {journal?.ip || "—"}
                            </span>
                          </td>

                          <td className="hideOnMobile">
                            <span
                              className={`adminJournalBadge ${
                                archived ? "badgeArchived" : "badgeNotArchived"
                              }`}
                            >
                              {archived ? "Archivé" : "Non archivé"}
                            </span>
                          </td>

                          <td className="colActions">
                            <div className="adminJournalActions">
                              <button
                                type="button"
                                className="adminJournalIconBtn"
                                title={archived ? "Désarchiver" : "Archiver"}
                                disabled={pendingId === journal?._id}
                                onClick={() => handleToggleArchive(journal)}
                              >
                                <i
                                  className={`bi ${
                                    archived
                                      ? "bi-arrow-counterclockwise"
                                      : "bi-archive"
                                  }`}
                                />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && filteredJournaux.length > 0 && (
              <div className="adminJournalPagination">
                <button
                  type="button"
                  className="adminJournalPageBtn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                >
                  <i className="bi bi-chevron-left" />
                </button>

                <div className="adminJournalPageInfo">
                  Page <b>{safePage}</b> / {totalPages}
                </div>

                <button
                  type="button"
                  className="adminJournalPageBtn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
